import {
  Box,
  Typography,
  Stack,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { fetchWithAuth } from "../../utils/api";
import RegularLinkButton from "./RegularLinkButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import fallbackImage from "../../assets/placeholder.png"; // <-- your default image
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ItemResult = ({ itemStats, mostRecentTradeDate, events = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const handleCopyTradeSummary = async () => {
    try {
      const response = await fetchWithAuth(
        `/item-summary/${encodeURIComponent(itemStats.name)}`
      );
      await navigator.clipboard.writeText(response.raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // silently ignore
    }
  };

  if (!itemStats) return null;

  // small data fix
  if (itemStats.name === "Dyeworks White: Prism Light Filter") {
    itemStats.name = "Dyeworks White : Prism Light Filter";
  }
  else if (itemStats.name === "Dyeworks Blue: Rainbow Petal Shower") {
    itemStats.name = "Dyeworks Blue:Rainbow Petal Shower";
  }

  const isStupidItem = itemStats.name === "Dyeworks Blue: Rainbow Petal Shower" || itemStats.name === "Dyeworks Blue:Rainbow Petal Shower";
  const stupidItemName = isStupidItem ? "Dyeworks Blue: Rainbow Petal Shower" : "";

  return (
    <Box sx={{ position: "relative" }}>
      <Paper
        variant="outlined"
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          position: "relative",
        }}
      >
        {/* Copy button — stable top-right placement */}
        <Tooltip title={copied ? "Copied!" : "Copy full trade summary"}>
          <IconButton
            size="small"
            onClick={handleCopyTradeSummary}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Item title */}
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: theme.palette.accent.main,
            wordBreak: "break-word",
            pr: 5, // leaves room for the copy icon
          }}
        >
          {itemStats.name}
        </Typography>

        {/* External Links */}
        <Stack
          direction="row"
          spacing={2}
          mb={1}
          sx={{ flexWrap: "wrap", rowGap: 1 }}
        >
          <RegularLinkButton
            linkURL={`https://items.jellyneo.net/search/?name=${encodeURIComponent(
              itemStats.name
            )}&name_type=3`}
            text="jn"
            iconURL={require("../../assets/link-icons/jn_icon.png")}
            iconSize={20}
            alt="jelly kacheek icon for jn"
            openNewTab
          />
          {itemStats.type === "wearable" &&
            (<RegularLinkButton
              linkURL={isStupidItem ? `https://impress.openneo.net/items?q=${encodeURIComponent(
                stupidItemName).replace(/%20/g, "+")}` :
                `https://impress.openneo.net/items?q=${encodeURIComponent(
                  itemStats.name
                ).replace(/%20/g, "+")}`}
              text="dti"
              iconURL={require("../../assets/link-icons/monocle_icon.png")}
              iconSize={20}
              alt="monocle icon for dti"
              openNewTab
            />)}
          <RegularLinkButton
            linkURL={isStupidItem ? `https://itemdb.com.br/item/dyeworks-bluerainbow-petal-shower` :
              `https://itemdb.com.br/item/${itemStats.name
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/gi, "-")
                .replace(/^-+|-+$/g, "")}`}
            text="itemdb"
            iconURL={require("../../assets/link-icons/itemdb.svg").default}
            iconSize={20}
            alt="itemdb icon"
            openNewTab
          />
          <RegularLinkButton
            linkURL={`https://www.neopets.com/safetydeposit.phtml?obj_name=${encodeURIComponent(
              itemStats.name
            ).replace(/%20/g, "+")}&category=0`}
            text="sdb"
            iconURL={require("../../assets/link-icons/sdb_icon.png")}
            iconSize={20}
            alt="sdb icon"
            openNewTab
          />
          {itemStats.type === "wearable" && (
            <RegularLinkButton
              linkURL={`https://www.neopets.com/closet.phtml?obj_name=${encodeURIComponent(
                itemStats.name
              ).replace(/%20/g, "+")}`}
              text="closet"
              iconURL={require("../../assets/link-icons/closet_icon.png")}
              iconSize={20}
              alt="closet icon"
              openNewTab
            />)}
        </Stack>

        {/* Stats + desktop image layout */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ xs: "flex-start", md: "center" }}
          py={1}
        >
          {/* Image on left for desktop */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={itemStats.imageUrl ? itemStats.imageUrl : fallbackImage}
              alt={itemStats.name}
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Item stats */}
          <Box flexGrow={1}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >

            {/* Item Stats */}
            <Stack spacing={1}>
              <Typography>
                Calculated value:{" "}
                <strong>
                  {itemStats.value === null || itemStats.value === "null"
                    ? "N/A"
                    : itemStats.value}
                  {itemStats.isVolatile ? "*" : ""}
                </strong>
              </Typography>
              <Typography>
                Last value update:{" "}
                {itemStats.lastUpdated === "unknown"
                  ? "N/A"
                  : itemStats.lastUpdated}
              </Typography>
              <Typography>
                Last trade: {mostRecentTradeDate || "-"}
              </Typography>
              {itemStats.notes && itemStats.notes !== "" &&
                <Typography>
                  Notes: {itemStats.notes}
                </Typography>
              }
            </Stack>
          </Box>
        </Stack>
        {itemStats.type === "token" && (
          <Alert
            severity="warning"
            variant="standard"
            sx={{
              py: 0.5,
              mt: 1,
              fontSize: "0.875rem",
              "& .MuiAlert-icon": {
                alignSelf: "center",
              },
            }}
          >
          Heads up! Deluxe supplies are now available for regular (1400nc) and prismatic (450nc)  styles - no need to gamble. Check which styles are in rotation, you may be able to trade 1:1 for supplies. 
          </Alert>
        )}
        {itemStats.isVolatile && (
          <Alert
            severity="warning"
            variant="standard"
            sx={{ py: 0.5, mt: 1, fontSize: "0.875rem" }}
          >
            We've detected potential value volatility in the reports for this
            item. Please review carefully.
          </Alert>
        )}
        {events.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Accordion
              defaultExpanded
              disableGutters
              sx={{
                mt: 2,
                mb: 0,
                boxShadow: "none",
                borderRadius: 2,
                overflow: "hidden",

                "&:before": {
                  display: "none",
                },

                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 40,
                  "&.Mui-expanded": {
                    minHeight: 40,
                  },

                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },

                  "& .MuiAccordionSummary-content.Mui-expanded": {
                    margin: "8px 0",
                  },
                }}
              >
                <Box display="flex" alignItems="center">
                  <Typography fontWeight="bold">
                    Key Events
                  </Typography>

                  <IconButton size="small" onClick={handleOpen} sx={{ ml: 1 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>

                  <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <Box sx={{ p: 2, maxWidth: 360 }}>
                      <Typography variant="body2">
                        <b>Live Beta:</b> This is not a full list of rereleases, just highlights that may be useful for value consideration.
                      </Typography>

                      <Typography
                        component="a"
                        href="/articles/how-a-value-guide-works#key-events"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: "primary.main",
                          textDecoration: "none"
                        }}
                      >
                        Learn more
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box
                  sx={{
                    maxHeight: 100,
                    overflowY: "auto",
                    // Firefox
                    scrollbarWidth: "none",

                    // IE/Edge Legacy
                    msOverflowStyle: "none",

                    // Chrome/Safari/Edge
                    "&::-webkit-scrollbar": {
                      display: "none"
                    },
                    pr: 1
                  }}
                >
                  {events?.map((event) => (
                    <Box key={event.id} sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.secondary.main
                        }}
                      >
                        {event.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          ml: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            mr: 1,
                            lineHeight: 1.5,
                          }}
                        >
                          •
                        </Typography>
                        <Typography
                          variant="body2"
                        >
                          {event.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Paper>
    </Box >
  );
};

export default ItemResult;